import React, { createContext, useContext, useMemo } from 'react';

const UserAccessContext = createContext(null);

export const UserAccessProvider = ({ children }) => {
    let storedUserInfo = null;
    try {
        storedUserInfo = JSON.parse(localStorage.getItem('UserInfo'));
    } catch {
        storedUserInfo = null;
    }
    const accessControl = useMemo(() => {
        const userInfo = storedUserInfo?.user_info;
        const pageAccessMap = (storedUserInfo?.page_access || []).reduce((acc, item) => {
            acc[item.page] = item.access_type;
            return acc;
        }, {});

        return {
            globalAccessType: userInfo?.accessType || 'Read',
            pageAccessMap,
            userInfo,

            getUserType: () => {
                return userInfo?.userType;
            },

            hasEditAccess: (pageName) => {
                const globalAccess = userInfo?.accessType;

                // If global access is Read or Read-Only, deny edit access for all pages
                if (globalAccess === 'Read' || globalAccess === 'Read-Only') {
                    return false;
                }

                // If global access is Read-Write, check page-specific access
                if (globalAccess === 'Read-Write') {
                    const pageAccess = pageAccessMap[pageName];
                    return pageAccess === 'Read-Write';
                }

                // Default to false
                return false;
            },

            // Check if user has any access to a page
            hasPageAccess: (pageName) => {
                return pageAccessMap.hasOwnProperty(pageName);
            },

            getPageAccessType: (pageName) => {
                const globalAccess = userInfo?.accessType;

                // If global access is Read or Read-Only, return Read-Only for all pages
                if (globalAccess === 'Read' || globalAccess === 'Read-Only') {
                    return 'Read-Only';
                }

                // If global access is Read-Write, check page-specific access
                if (globalAccess === 'Read-Write') {
                    const pageAccess = pageAccessMap[pageName];
                    return pageAccess === 'Read-Write' ? 'Read-Write' : 'Read-Only';
                }

                return 'Read-Only';
            }
        };
    }, [storedUserInfo]);

    return (
        <UserAccessContext.Provider value={accessControl}>
            {children}
        </UserAccessContext.Provider>
    );
};

export const useUserAccess = () => {
    const context = useContext(UserAccessContext);
    if (!context) {
        throw new Error('useUserAccess must be used within UserAccessProvider');
    }
    return context;
};